import { useState } from 'react'

interface LoginPageProps {
  base: string
  onLoginSuccess: (authInfo: { email: string; role: string | null; mode?: string }) => void
}

export default function LoginPage({ base, onLoginSuccess }: LoginPageProps) {
  const [authEmail, setAuthEmail] = useState<string>('')
  const [authPassword, setAuthPassword] = useState<string>('')
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [challengeSession, setChallengeSession] = useState<string | null>(null)
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetEmail, setResetEmail] = useState<string>('')
  const [resetCode, setResetCode] = useState<string>('')
  const [resetNewPassword, setResetNewPassword] = useState<string>('')
  const [resetConfirmPassword, setResetConfirmPassword] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleLogin = async () => {
    setError(null)
    setIsLoading(true)
    try {
      const resp = await fetch(`${base}/authc/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: authEmail, password: authPassword })
      })

      if (resp.status === 403) {
        const d = await resp.json()
        if (d.challenge === 'NEW_PASSWORD_REQUIRED') {
          setChallengeSession(d.session)
          setShowPasswordChange(true)
          setIsLoading(false)
          return
        }
      }

      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}))
        throw new Error(d.error || 'Login failed')
      }

      const me = await fetch(`${base}/authc/whoami`, { credentials: 'include' })
      if (me.ok) {
        const d = await me.json()
        onLoginSuccess({ email: d.email, role: d.role ?? null, mode: d.mode })
      }
    } catch (e: any) {
      setError(e?.message || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setError(null)
    setIsLoading(true)
    try {
      const resp = await fetch(`${base}/authc/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: authEmail,
          password: authPassword,
          newPassword,
          session: challengeSession
        })
      })
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}))
        throw new Error(d.error || 'Password change failed')
      }
      const me = await fetch(`${base}/authc/whoami`, { credentials: 'include' })
      if (me.ok) {
        const d = await me.json()
        onLoginSuccess({ email: d.email, role: d.role ?? null, mode: d.mode })
      }
    } catch (e: any) {
      setError(e?.message || 'Password change failed')
    } finally {
      setIsLoading(false)
    }
  }

  const handleForgotPassword = async () => {
    if (!resetEmail) {
      setError('Please enter your email address')
      return
    }
    setError(null)
    setIsLoading(true)
    try {
      const resp = await fetch(`${base}/auth/forgot`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      })
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}))
        throw new Error(d.error || 'Request failed')
      }
      setShowForgotPassword(false)
      setShowResetPassword(true)
    } catch (e: any) {
      setError(e?.message || 'Failed to send reset code')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResetPassword = async () => {
    if (!resetCode || !resetNewPassword) {
      setError('Please enter the code and new password')
      return
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (resetNewPassword.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    setError(null)
    setIsLoading(true)
    try {
      const resp = await fetch(`${base}/auth/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail, code: resetCode, newPassword: resetNewPassword })
      })
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}))
        throw new Error(d.error || 'Reset failed')
      }
      // Reset state and go back to login
      setShowResetPassword(false)
      setResetEmail('')
      setResetCode('')
      setResetNewPassword('')
      setResetConfirmPassword('')
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to reset password')
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (showPasswordChange) {
        handlePasswordChange()
      } else if (showForgotPassword) {
        handleForgotPassword()
      } else if (showResetPassword) {
        handleResetPassword()
      } else {
        handleLogin()
      }
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Logo and Title */}
          <div className="mb-8 text-center">
            <img src="/logo.svg" alt="ESF Logo" className="h-12 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-gray-900">Digital Product Passport</h1>
            <p className="text-sm text-gray-500 mt-1">Environmental Street Furniture</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-error-50 border border-error-200 text-error-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {showPasswordChange ? (
            <>
              <div className="mb-1 text-lg font-semibold text-gray-900">Change Password</div>
              <div className="mb-6 text-sm text-gray-500">Your temporary password must be changed</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Enter new password"
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Confirm new password"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button
                  className="btn btn-primary w-full"
                  onClick={handlePasswordChange}
                  disabled={isLoading}
                >
                  {isLoading ? 'Changing...' : 'Change Password'}
                </button>
                <button
                  className="btn btn-outline w-full"
                  onClick={() => {
                    setShowPasswordChange(false)
                    setChallengeSession(null)
                    setNewPassword('')
                    setConfirmPassword('')
                    setError(null)
                  }}
                >
                  Cancel
                </button>
              </div>
            </>
          ) : showForgotPassword ? (
            <>
              <div className="mb-1 text-lg font-semibold text-gray-900">Forgot Password</div>
              <div className="mb-6 text-sm text-gray-500">Enter your email to receive a reset code</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Enter your email"
                    type="email"
                    value={resetEmail}
                    onChange={e => setResetEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button
                  className="btn btn-primary w-full"
                  onClick={handleForgotPassword}
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Code'}
                </button>
                <button
                  className="btn btn-outline w-full"
                  onClick={() => {
                    setShowForgotPassword(false)
                    setResetEmail('')
                    setError(null)
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            </>
          ) : showResetPassword ? (
            <>
              <div className="mb-1 text-lg font-semibold text-gray-900">Reset Password</div>
              <div className="mb-6 text-sm text-gray-500">Enter the code sent to {resetEmail}</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Enter 6-digit code"
                    value={resetCode}
                    onChange={e => setResetCode(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Enter new password"
                    type="password"
                    value={resetNewPassword}
                    onChange={e => setResetNewPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Confirm new password"
                    type="password"
                    value={resetConfirmPassword}
                    onChange={e => setResetConfirmPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <button
                  className="btn btn-primary w-full"
                  onClick={handleResetPassword}
                  disabled={isLoading}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
                <button
                  className="btn btn-outline w-full"
                  onClick={() => {
                    setShowResetPassword(false)
                    setResetEmail('')
                    setResetCode('')
                    setResetNewPassword('')
                    setResetConfirmPassword('')
                    setError(null)
                  }}
                >
                  Back to Sign In
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="mb-1 text-lg font-semibold text-gray-900">Welcome back</div>
              <div className="mb-6 text-sm text-gray-500">Please sign in to your account</div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Enter your email"
                    type="email"
                    value={authEmail}
                    onChange={e => setAuthEmail(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                  <input
                    className="filter-input w-full"
                    placeholder="Enter your password"
                    type="password"
                    value={authPassword}
                    onChange={e => setAuthPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-gray-600">
                    <input type="checkbox" className="rounded border-gray-300" />
                    Remember me
                  </label>
                  <button
                    type="button"
                    className="text-esf-navy hover:text-esf-navy-dark font-medium"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Forgot password?
                  </button>
                </div>
                <button
                  className="btn btn-primary w-full"
                  onClick={handleLogin}
                  disabled={isLoading}
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </button>
              </div>
            </>
          )}

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400">
              Protected by role-based access control
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
